import { Api } from "src/utils/api";
import { CFO_API_BASE_URL } from "./cfo.constant";

export interface CfoExecutiveSummaryData {
  available_capital: number;
  frozen_capital: number;
  borrowed_capital: number;
  lost_sales: number;
  contribution_profit: number;
  gross_margin: number;
  contribution_margin: number;
  net_margin: number;
  opex: number;
  ebitda: number;
  roi: number;
}

export interface CfoFinancialData {
  total_sales: CfoTableItem;
  cost_of_goods_sold: CfoTableItem;
  overhead_cost: CfoTableItem;
  gross_profit: CfoTableItem;
  gross_margin: CfoTableItem;
  contribution_profit: CfoTableItem;
  contribution_margin: CfoTableItem;
  opex: CfoTableItem;
  net_profit: CfoTableItem;
  net_margin: CfoTableItem;
  roi: CfoTableItem;
  acos: CfoTableItem;
  tacos: CfoTableItem;
}

export interface CfoTableItem {
  value: number;
  components: {
    amazon: number;
    shopify: number;
    tiktok: number;
  };
}

export interface CashFlowTableItem {
  period_start: string;
  period_end: string;
  total_sales: CashFlowValue | number;
  cost_of_goods_sold: CashFlowValue | number;
  overhead_cost: CashFlowValue | number;
  gross_profit: CashFlowValue | number;
  gross_margin: CashFlowValue | number;
  contribution_profit: CashFlowValue | number;
  contribution_margin: CashFlowValue | number;
  opex: CashFlowValue | number;
  net_profit: CashFlowValue | number;
  net_margin: CashFlowValue | number;
  roi: CashFlowValue | number;
  acos: CashFlowValue | number;
  tacos: CashFlowValue | number;
  cash_flow: CashFlowValue | number;
}

export interface CashFlowValue {
  value: number;
  components: {
    amazon: number;
    shopify: number;
    tiktok: number;
  };
}

export type CashFlowTableData = CashFlowTableItem[];

export interface CfoCashFlowChartData {
  periods: string[];
  metrics: Record<string, number[]>;
}

export interface CfoWaterfallChartData {
  total_sales: number;
  cost_of_goods_sold: number;
  overhead_cost: number;
  opex: number;
  net_profit: number;
}

export class CfoApi extends Api {
  constructor() {
    super(CFO_API_BASE_URL);
  }

  async getExecutiveSummary(
    startDate: string,
    endDate: string,
    asin?: string,
    cogs = 0,
    amazonFees = 0,
    misc = 0,
  ): Promise<CfoExecutiveSummaryData> {
    return this.get<CfoExecutiveSummaryData>("/executive-summary", {
      date_start: startDate,
      date_end: endDate,
      ...(asin ? { asin } : {}),
      cogs,
      amazon_fees: amazonFees,
      misc,
    });
  }

  async getFinancialData(
    startDate: string,
    endDate: string,
    asin: string,
    cogs: number = 0,
    amazonFees: number = 0,
    misc: number = 0,
  ): Promise<CfoFinancialData> {
    try {
      const response = await this.get<CfoFinancialData>(`/financial-overview`, {
        date_start: startDate,
        date_end: endDate,
        asin,
        cogs,
        amazon_fees: amazonFees,
        misc,
      });
      return response;
    } catch (error) {
      console.error("Failed to fetch CFO financial data:", error);
      return {
        total_sales: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        cost_of_goods_sold: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        overhead_cost: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        gross_profit: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        gross_margin: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        contribution_profit: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        contribution_margin: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        opex: { value: 0, components: { amazon: 0, shopify: 0, tiktok: 0 } },
        net_profit: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        net_margin: {
          value: 0,
          components: { amazon: 0, shopify: 0, tiktok: 0 },
        },
        roi: { value: 0, components: { amazon: 0, shopify: 0, tiktok: 0 } },
        acos: { value: 0, components: { amazon: 0, shopify: 0, tiktok: 0 } },
        tacos: { value: 0, components: { amazon: 0, shopify: 0, tiktok: 0 } },
      };
    }
  }

  async getCashFlowTableData({
    startDate,
    endDate,
    asin,
    cogs = 0,
    amazonFees = 0,
    misc = 0,
  }: {
    startDate: string;
    endDate: string;
    asin: string;
    cogs?: number;
    amazonFees?: number;
    misc?: number;
    period: "day" | "week" | "month";
  }): Promise<CashFlowTableData> {
    try {
      const response = await this.get<CashFlowTableData>(`/cash-flow-table`, {
        date_start: startDate,
        date_end: endDate,
        asin,
        cogs,
        amazon_fees: amazonFees,
        misc,
      });
      return response;
    } catch (error) {
      console.error("Failed to fetch CFO cash flow table data:", error);
      return [];
    }
  }

  async getCashFlowChartData({
    startDate,
    endDate,
    asin = "",
    cogs = 0,
    amazonFees = 0,
    misc = 0,
    period,
  }: {
    startDate: string;
    endDate: string;
    asin: string;
    cogs?: number;
    amazonFees?: number;
    misc?: number;
    period: "day" | "week" | "month";
  }): Promise<CfoCashFlowChartData> {
    try {
      const response = await this.get<CfoCashFlowChartData>(
        `/cash-flow-chart`,
        {
          date_start: startDate,
          date_end: endDate,
          asin,
          cogs,
          amazon_fees: amazonFees,
          misc,
          period,
        },
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch CFO cash flow chart data:", error);
      return { periods: [], metrics: {} };
    }
  }

  async getWaterfallChartData({
    startDate,
    endDate,
    asin = "",
    cogs = 0,
    amazonFees = 0,
    misc = 0,
    salesChannel = "Amazon",
  }: {
    startDate: string;
    endDate: string;
    asin?: string;
    cogs?: number;
    amazonFees?: number;
    misc?: number;

    salesChannel?: "Amazon" | "Shopify" | "TikTok";
  }): Promise<CfoWaterfallChartData> {
    try {
      const response = await this.get<CfoWaterfallChartData>(
        `/waterfall-chart`,
        {
          date_start: startDate,
          date_end: endDate,
          asin,
          cogs,
          amazon_fees: amazonFees,
          misc,
          sales_channel: salesChannel,
        },
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch CFO waterfall chart data:", error);
      return {
        total_sales: 0,
        cost_of_goods_sold: 0,
        overhead_cost: 0,
        opex: 0,
        net_profit: 0,
      };
    }
  }
}
